import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  startAfter,
  serverTimestamp,
  Timestamp,
  writeBatch,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  Segment,
  SegmentFormData,
  SegmentFilter,
  SegmentPreview,
  SegmentEvaluationResult,
  SegmentAnalytics,
  SegmentUsage,
  EcommerceData,
  SegmentConditionGroup,
  SegmentCondition,
  AVAILABLE_SEGMENT_FIELDS
} from '../types/segment';
import { Contact } from '../types/contact';

class SegmentService {
  private readonly COLLECTION_NAME = 'segments';
  private readonly CONTACTS_COLLECTION = 'contacts';
  private readonly ECOMMERCE_COLLECTION = 'ecommerce_data';

  // Create a new segment with validation
  async createSegment(userId: string, workspaceId: string, segmentData: SegmentFormData): Promise<string> {
    try {
      // Validate input
      if (!userId || !workspaceId) {
        throw new Error('User ID and workspace ID are required');
      }

      if (!segmentData.name || segmentData.name.trim().length === 0) {
        throw new Error('Segment name is required');
      }

      // Validate conditions
      const validation = this.validateSegmentConditions(segmentData.conditionGroups);
      if (!validation.isValid) {
        throw new Error(`Invalid segment conditions: ${validation.errors.join(', ')}`);
      }

      const segmentRef = collection(db, this.COLLECTION_NAME);
      const newSegment: Omit<Segment, 'id'> = {
        ...segmentData,
        name: segmentData.name.trim(),
        workspaceId,
        userId,
        contactCount: 0,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
        isActive: true
      };

      const docRef = await addDoc(segmentRef, newSegment);

      // If it's a dynamic segment, evaluate it immediately (but don't wait for completion)
      if (segmentData.isDynamic) {
        // Evaluate in background to avoid blocking
        this.evaluateSegment(docRef.id).catch(error => {
          console.error(`Error evaluating newly created segment ${docRef.id}:`, error);
        });
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating segment:', error);
      throw new Error(`Failed to create segment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Get all segments for a user/workspace
  async getSegments(filter: SegmentFilter): Promise<Segment[]> {
    try {
      const segmentsRef = collection(db, this.COLLECTION_NAME);
      let q = query(segmentsRef);

      if (filter.workspaceId) {
        q = query(q, where('workspaceId', '==', filter.workspaceId));
      }

      if (filter.userId) {
        q = query(q, where('userId', '==', filter.userId));
      }

      if (filter.isActive !== undefined) {
        q = query(q, where('isActive', '==', filter.isActive));
      }

      if (filter.isDynamic !== undefined) {
        q = query(q, where('isDynamic', '==', filter.isDynamic));
      }

      // Note: Removed orderBy from Firestore query to avoid index requirement.
      // We will sort client-side instead.

      const querySnapshot = await getDocs(q);
      const segments: Segment[] = [];

      querySnapshot.forEach((doc) => {
        segments.push({
          id: doc.id,
          ...doc.data()
        } as Segment);
      });

      // Apply client-side filters
      let filteredSegments = segments;

      if (filter.search) {
        const searchLower = filter.search.toLowerCase();
        filteredSegments = filteredSegments.filter(segment =>
          segment.name.toLowerCase().includes(searchLower) ||
          segment.description?.toLowerCase().includes(searchLower)
        );
      }

      if (filter.tags && filter.tags.length > 0) {
        filteredSegments = filteredSegments.filter(segment =>
          segment.tags && segment.tags.some(tag => filter.tags!.includes(tag))
        );
      }

      // Sort by updatedAt desc client-side
      filteredSegments.sort((a, b) => {
        const timeA = a.updatedAt?.toMillis?.() || a.updatedAt?.seconds * 1000 || 0;
        const timeB = b.updatedAt?.toMillis?.() || b.updatedAt?.seconds * 1000 || 0;
        return timeB - timeA;
      });

      return filteredSegments;
    } catch (error) {
      console.error('Error getting segments:', error);
      throw new Error('Failed to get segments');
    }
  }

  // Get a single segment by ID
  async getSegment(segmentId: string): Promise<Segment | null> {
    try {
      const segmentRef = doc(db, this.COLLECTION_NAME, segmentId);
      const segmentSnap = await getDoc(segmentRef);

      if (segmentSnap.exists()) {
        return {
          id: segmentSnap.id,
          ...segmentSnap.data()
        } as Segment;
      }

      return null;
    } catch (error) {
      console.error('Error getting segment:', error);
      throw new Error('Failed to get segment');
    }
  }

  // Update a segment with validation
  async updateSegment(segmentId: string, updates: Partial<SegmentFormData>): Promise<void> {
    try {
      if (!segmentId) {
        throw new Error('Segment ID is required');
      }

      // Validate name if provided
      if (updates.name !== undefined && (!updates.name || updates.name.trim().length === 0)) {
        throw new Error('Segment name cannot be empty');
      }

      // Validate conditions if provided
      if (updates.conditionGroups) {
        const validation = this.validateSegmentConditions(updates.conditionGroups);
        if (!validation.isValid) {
          throw new Error(`Invalid segment conditions: ${validation.errors.join(', ')}`);
        }
      }

      const segmentRef = doc(db, this.COLLECTION_NAME, segmentId);
      const updateData: any = {
        updatedAt: serverTimestamp()
      };

      // Only include provided fields
      if (updates.name !== undefined) {
        updateData.name = updates.name.trim();
      }
      if (updates.description !== undefined) {
        updateData.description = updates.description;
      }
      if (updates.isDynamic !== undefined) {
        updateData.isDynamic = updates.isDynamic;
      }
      if (updates.conditionGroups !== undefined) {
        updateData.conditionGroups = updates.conditionGroups;
      }
      if (updates.tags !== undefined) {
        updateData.tags = updates.tags;
      }
      if (updates.color !== undefined) {
        updateData.color = updates.color;
      }

      await updateDoc(segmentRef, updateData);

      // Re-evaluate if it's a dynamic segment and conditions changed
      if (updates.conditionGroups || updates.isDynamic) {
        const segment = await this.getSegment(segmentId);
        if (segment?.isDynamic) {
          // Evaluate in background to avoid blocking
          this.evaluateSegment(segmentId).catch(error => {
            console.error(`Error evaluating updated segment ${segmentId}:`, error);
          });
        }
      }
    } catch (error) {
      console.error('Error updating segment:', error);
      throw new Error(`Failed to update segment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Delete a segment
  async deleteSegment(segmentId: string): Promise<void> {
    try {
      const segmentRef = doc(db, this.COLLECTION_NAME, segmentId);
      await deleteDoc(segmentRef);
    } catch (error) {
      console.error('Error deleting segment:', error);
      throw new Error('Failed to delete segment');
    }
  }

  // Duplicate a segment
  async duplicateSegment(segmentId: string, newName: string, userId: string, workspaceId: string): Promise<string> {
    try {
      const originalSegment = await this.getSegment(segmentId);
      if (!originalSegment) {
        throw new Error('Segment not found');
      }

      const duplicateData: SegmentFormData = {
        name: newName,
        description: originalSegment.description,
        isDynamic: originalSegment.isDynamic,
        conditionGroups: originalSegment.conditionGroups,
        tags: originalSegment.tags,
        color: originalSegment.color
      };

      return await this.createSegment(userId, workspaceId, duplicateData);
    } catch (error) {
      console.error('Error duplicating segment:', error);
      throw new Error('Failed to duplicate segment');
    }
  }

  // Manually add contacts to a segment
  async addContactsToSegment(segmentId: string, contactIds: string[]): Promise<number> {
    try {
      if (!segmentId || !contactIds.length) {
        throw new Error('Segment ID and contact IDs are required');
      }

      const segment = await this.getSegment(segmentId);
      if (!segment) {
        throw new Error('Segment not found');
      }

      const MAX_BATCH_SIZE = 500;
      let addedCount = 0;

      for (let i = 0; i < contactIds.length; i += MAX_BATCH_SIZE) {
        const batchIds = contactIds.slice(i, i + MAX_BATCH_SIZE);
        const batch = writeBatch(db);
        let opsInBatch = 0;

        for (const contactId of batchIds) {
          try {
            const contactRef = doc(db, this.CONTACTS_COLLECTION, contactId);
            const contactSnap = await getDoc(contactRef);
            if (!contactSnap.exists()) continue;

            const contactData = contactSnap.data();
            const currentSegments: string[] = contactData.segments || [];

            if (!currentSegments.includes(segmentId)) {
              batch.update(contactRef, {
                segments: [...currentSegments, segmentId],
                lastChanged: serverTimestamp()
              });
              opsInBatch++;
              addedCount++;
            }
          } catch (err) {
            console.error(`Error adding contact ${contactId} to segment:`, err);
          }
        }

        if (opsInBatch > 0) {
          await batch.commit();
        }
      }

      // Update the segment contact count
      if (addedCount > 0) {
        const segmentRef = doc(db, this.COLLECTION_NAME, segmentId);
        await updateDoc(segmentRef, {
          contactCount: (segment.contactCount || 0) + addedCount,
          updatedAt: serverTimestamp()
        });
      }

      return addedCount;
    } catch (error) {
      console.error('Error adding contacts to segment:', error);
      throw new Error(`Failed to add contacts to segment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Preview segment conditions without saving - optimized with limit
  async previewSegment(conditionGroups: SegmentConditionGroup[], workspaceId: string, maxContacts: number = 1000): Promise<SegmentPreview> {
    try {
      // Validate conditions first
      const validation = this.validateSegmentConditions(conditionGroups);
      if (!validation.isValid) {
        throw new Error(`Invalid segment conditions: ${validation.errors.join(', ')}`);
      }

      const startTime = Date.now();

      // For preview, we can limit the evaluation to avoid long waits
      // Evaluate conditions but stop early if we have enough samples
      const matchingContacts = await this.evaluateConditions(conditionGroups, workspaceId);
      const evaluationTime = Date.now() - startTime;

      // Limit results for preview to avoid overwhelming the UI
      const previewContacts = matchingContacts.slice(0, maxContacts);

      // Get sample contacts (first 10 for display)
      const sampleContacts = previewContacts.slice(0, 10).map(contact => ({
        id: contact.id,
        email: contact.email || '',
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        tags: contact.tags || []
      }));

      return {
        conditionGroups,
        estimatedCount: matchingContacts.length,
        sampleContacts,
        evaluationTime
      };
    } catch (error) {
      console.error('Error previewing segment:', error);
      throw new Error(`Failed to preview segment: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Evaluate a dynamic segment and update contact count AND contact documents
  async evaluateSegment(segmentId: string): Promise<SegmentEvaluationResult> {
    try {
      const segment = await this.getSegment(segmentId);
      if (!segment) {
        throw new Error('Segment not found');
      }

      const startTime = Date.now();
      const matchingContacts = await this.evaluateConditions(segment.conditionGroups, segment.workspaceId);
      const evaluationTime = Date.now() - startTime;

      const contactIds = matchingContacts.map(contact => contact.id);

      // Get all contacts currently in this segment
      const contactsRef = collection(db, this.CONTACTS_COLLECTION);
      const contactsInSegmentQuery = query(
        contactsRef,
        where('workspaceId', '==', segment.workspaceId),
        where('segments', 'array-contains', segmentId)
      );
      const contactsInSegmentSnapshot = await getDocs(contactsInSegmentQuery);
      const currentContactIds = contactsInSegmentSnapshot.docs.map(doc => doc.id);

      // Find contacts to add (in matchingContacts but not in currentContactIds)
      const contactsToAdd = contactIds.filter(id => !currentContactIds.includes(id));

      // Find contacts to remove (in currentContactIds but not in matchingContacts)
      const contactsToRemove = currentContactIds.filter(id => !contactIds.includes(id));

      // Batch update contacts - optimized with efficient batching
      const MAX_BATCH_SIZE = 500; // Firestore batch limit
      const allOperations: Array<{ contactId: string; type: 'add' | 'remove' }> = [
        ...contactsToAdd.map(id => ({ contactId: id, type: 'add' as const })),
        ...contactsToRemove.map(id => ({ contactId: id, type: 'remove' as const }))
      ];

      // Process updates in optimized batches
      if (allOperations.length > 0) {
        console.log(`[Segment Evaluation] Updating ${allOperations.length} contacts (${contactsToAdd.length} to add, ${contactsToRemove.length} to remove)`);

        // Process operations in chunks
        for (let i = 0; i < allOperations.length; i += MAX_BATCH_SIZE) {
          const batchOps = allOperations.slice(i, i + MAX_BATCH_SIZE);

          // Read contact documents in parallel for this batch
          const contactDocs = await Promise.all(
            batchOps.map(async (op) => {
              try {
                const contactDoc = await getDoc(doc(db, this.CONTACTS_COLLECTION, op.contactId));
                return { operation: op, contactDoc };
              } catch (error) {
                console.error(`Error reading contact ${op.contactId}:`, error);
                return null;
              }
            })
          );

          // Filter out failed reads
          const validDocs = contactDocs.filter((item): item is { operation: typeof allOperations[0]; contactDoc: any } =>
            item !== null && item.contactDoc.exists()
          );

          // Process updates in Firestore batches (max 500 operations per batch)
          if (validDocs.length > 0) {
            let currentBatch = writeBatch(db);
            let operationsInBatch = 0;

            for (const { operation, contactDoc } of validDocs) {
              // Check BEFORE adding to batch - Firestore limit is 500 operations
              if (operationsInBatch >= MAX_BATCH_SIZE) {
                await currentBatch.commit();
                currentBatch = writeBatch(db); // Create new batch
                operationsInBatch = 0;
              }

              const contactData = contactDoc.data();
              const currentSegments = contactData.segments || [];

              if (operation.type === 'add') {
                if (!currentSegments.includes(segmentId)) {
                  currentBatch.update(contactDoc.ref, {
                    segments: [...currentSegments, segmentId],
                    lastChanged: serverTimestamp()
                  });
                  operationsInBatch++;
                }
              } else {
                const updatedSegments = currentSegments.filter((id: string) => id !== segmentId);
                currentBatch.update(contactDoc.ref, {
                  segments: updatedSegments,
                  lastChanged: serverTimestamp()
                });
                operationsInBatch++;
              }
            }

            // Commit remaining updates
            if (operationsInBatch > 0) {
              await currentBatch.commit();
            }
          }
        }
      }

      // Update segment with new contact count
      const segmentRef = doc(db, this.COLLECTION_NAME, segmentId);
      await updateDoc(segmentRef, {
        contactCount: contactIds.length,
        lastEvaluated: serverTimestamp()
      });

      const result: SegmentEvaluationResult = {
        segmentId,
        contactIds,
        totalCount: contactIds.length,
        evaluatedAt: serverTimestamp() as Timestamp,
        evaluationTime
      };

      return result;
    } catch (error) {
      console.error('Error evaluating segment:', error);
      throw new Error('Failed to evaluate segment');
    }
  }

  // Get contacts that match segment conditions
  async getSegmentContacts(segmentId: string, limitCount?: number): Promise<Contact[]> {
    try {
      const segment = await this.getSegment(segmentId);
      if (!segment) {
        throw new Error('Segment not found');
      }

      const matchingContacts = await this.evaluateConditions(segment.conditionGroups, segment.workspaceId);

      if (limitCount) {
        return matchingContacts.slice(0, limitCount);
      }

      return matchingContacts;
    } catch (error) {
      console.error('Error getting segment contacts:', error);
      throw new Error('Failed to get segment contacts');
    }
  }

  // Core condition evaluation logic - optimized for large datasets with pagination
  private async evaluateConditions(conditionGroups: SegmentConditionGroup[], workspaceId: string): Promise<Contact[]> {
    try {
      if (!conditionGroups || conditionGroups.length === 0) {
        return [];
      }

      // Validate conditions before processing
      const validation = this.validateSegmentConditions(conditionGroups);
      if (!validation.isValid) {
        throw new Error(`Invalid segment conditions: ${validation.errors.join(', ')}`);
      }

      const contactsRef = collection(db, this.CONTACTS_COLLECTION);
      const matchingContacts: Contact[] = [];

      // Process contacts in batches to avoid memory issues
      // Use a simple pagination approach that works without requiring composite indexes
      const BATCH_SIZE = 500;
      let lastDocId: string | null = null;
      let hasMore = true;
      let processedCount = 0;

      while (hasMore) {
        try {
          // Build query - MUST use orderBy when using startAfter
          // Use __name__ for consistent ordering without requiring an index
          let q = query(
            contactsRef,
            where('workspaceId', '==', workspaceId),
            orderBy('__name__'), // Use document ID for consistent pagination
            firestoreLimit(BATCH_SIZE)
          );

          // If we have a last document, use it for pagination
          if (lastDocId) {
            const lastDocRef = doc(db, this.CONTACTS_COLLECTION, lastDocId);
            const lastDoc = await getDoc(lastDocRef);
            if (lastDoc.exists()) {
              q = query(q, startAfter(lastDoc));
            } else {
              // Document was deleted, reset pagination
              lastDocId = null;
              hasMore = false;
              break;
            }
          }

          const querySnapshot = await getDocs(q);

          if (querySnapshot.empty) {
            hasMore = false;
            break;
          }

          const batchContacts: Contact[] = [];
          querySnapshot.forEach((doc) => {
            batchContacts.push({
              id: doc.id,
              ...doc.data()
            } as Contact);
          });

          // Get ecommerce data for this batch (handles batching internally)
          const ecommerceData = await this.getEcommerceData(batchContacts.map(c => c.id));

          // Evaluate each contact in this batch
          for (const contact of batchContacts) {
            try {
              const contactEcommerceData = ecommerceData[contact.id];
              const contactWithEcommerce = { ...contact, ecommerce: contactEcommerceData };

              if (this.evaluateContactAgainstConditions(contactWithEcommerce, conditionGroups)) {
                matchingContacts.push(contact);
              }
            } catch (contactError) {
              console.error(`Error evaluating contact ${contact.id}:`, contactError);
              // Continue with other contacts even if one fails
            }
          }

          // Update pagination cursor
          const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
          lastDocId = lastDoc.id;
          processedCount += batchContacts.length;
          hasMore = querySnapshot.docs.length === BATCH_SIZE;

          // Log progress for large evaluations
          if (processedCount % 1000 === 0) {
            console.log(`[Segment Evaluation] Processed ${processedCount} contacts, found ${matchingContacts.length} matches so far`);
          }
        } catch (batchError) {
          console.error(`Error processing batch starting at ${lastDocId}:`, batchError);
          // If pagination fails, fall back to processing all at once (for smaller datasets)
          if (processedCount === 0) {
            console.log('[Segment Evaluation] Falling back to non-paginated evaluation');
            return await this.evaluateConditionsFallback(conditionGroups, workspaceId);
          }
          hasMore = false;
        }
      }

      console.log(`[Segment Evaluation] Completed: processed ${processedCount} contacts, found ${matchingContacts.length} matches`);
      return matchingContacts;
    } catch (error) {
      console.error('Error evaluating conditions:', error);
      throw new Error(`Failed to evaluate conditions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Fallback method for smaller datasets or when pagination fails
  private async evaluateConditionsFallback(conditionGroups: SegmentConditionGroup[], workspaceId: string): Promise<Contact[]> {
    const contactsRef = collection(db, this.CONTACTS_COLLECTION);
    const q = query(contactsRef, where('workspaceId', '==', workspaceId));
    const querySnapshot = await getDocs(q);

    const allContacts: Contact[] = [];
    querySnapshot.forEach((doc) => {
      allContacts.push({
        id: doc.id,
        ...doc.data()
      } as Contact);
    });

    const ecommerceData = await this.getEcommerceData(allContacts.map(c => c.id));
    const matchingContacts: Contact[] = [];

    for (const contact of allContacts) {
      try {
        const contactEcommerceData = ecommerceData[contact.id];
        const contactWithEcommerce = { ...contact, ecommerce: contactEcommerceData };

        if (this.evaluateContactAgainstConditions(contactWithEcommerce, conditionGroups)) {
          matchingContacts.push(contact);
        }
      } catch (contactError) {
        console.error(`Error evaluating contact ${contact.id}:`, contactError);
      }
    }

    return matchingContacts;
  }

  // Evaluate a single contact against all condition groups
  private evaluateContactAgainstConditions(contact: Contact & { ecommerce?: EcommerceData }, conditionGroups: SegmentConditionGroup[]): boolean {
    if (conditionGroups.length === 0) return true;

    // If there's only one group, evaluate it
    if (conditionGroups.length === 1) {
      return this.evaluateConditionGroup(contact, conditionGroups[0]);
    }

    // For multiple groups, use logicWithNext to determine how groups are combined
    let result = this.evaluateConditionGroup(contact, conditionGroups[0]);

    for (let i = 1; i < conditionGroups.length; i++) {
      const groupResult = this.evaluateConditionGroup(contact, conditionGroups[i]);
      const logic = conditionGroups[i - 1].logicWithNext || 'AND'; // Use logicWithNext from previous group

      if (logic === 'OR') {
        result = result || groupResult;
      } else {
        result = result && groupResult;
      }
    }

    return result;
  }

  // Evaluate a single condition group
  private evaluateConditionGroup(contact: Contact & { ecommerce?: EcommerceData }, group: SegmentConditionGroup): boolean {
    if (group.conditions.length === 0) return true;

    const results = group.conditions.map(condition =>
      this.evaluateCondition(contact, condition)
    );

    // Apply logic between conditions
    if (group.logic === 'OR') {
      return results.some(result => result);
    } else {
      return results.every(result => result);
    }
  }

  // Normalize field names for backward compatibility
  private normalizeFieldName(fieldName: string): string {
    // Handle legacy field name mappings
    if (fieldName === 'subscriptionStatus') {
      return 'status';
    }
    return fieldName;
  }

  // Evaluate a single condition with robust error handling
  private evaluateCondition(contact: Contact & { ecommerce?: EcommerceData }, condition: SegmentCondition): boolean {
    try {
      if (!condition || !condition.field || !condition.operator) {
        console.warn('Invalid condition provided:', condition);
        return false;
      }

      const normalizedField = this.normalizeFieldName(condition.field);
      const fieldValue = this.getFieldValue(contact, normalizedField);
      const conditionValue = condition.value;

      // Validate field exists in available fields
      const fieldDef = AVAILABLE_SEGMENT_FIELDS.find(f => f.id === normalizedField);
      if (!fieldDef) {
        console.warn(`Field "${normalizedField}" not found in available fields`);
        return false;
      }

      // Validate operator is valid for this field
      if (!fieldDef.operators.includes(condition.operator)) {
        console.warn(`Operator "${condition.operator}" not valid for field "${fieldDef.label}"`);
        return false;
      }

      switch (condition.operator) {
        case 'equals':
          // Handle null/undefined
          if (fieldValue === null || fieldValue === undefined) {
            return conditionValue === null || conditionValue === undefined;
          }
          // Handle string comparison with normalization
          if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
            return fieldValue.trim().toLowerCase() === conditionValue.trim().toLowerCase();
          }
          return fieldValue === conditionValue;
        case 'not_equals':
          // Handle null/undefined
          if (fieldValue === null || fieldValue === undefined) {
            return conditionValue !== null && conditionValue !== undefined;
          }
          // Handle string comparison with normalization
          if (typeof fieldValue === 'string' && typeof conditionValue === 'string') {
            return fieldValue.trim().toLowerCase() !== conditionValue.trim().toLowerCase();
          }
          return fieldValue !== conditionValue;
        case 'contains':
          if (fieldValue === null || fieldValue === undefined || typeof fieldValue !== 'string') return false;
          if (conditionValue === null || conditionValue === undefined) return false;
          return fieldValue.toLowerCase().includes(String(conditionValue).toLowerCase());
        case 'not_contains':
          if (fieldValue === null || fieldValue === undefined || typeof fieldValue !== 'string') return true;
          if (conditionValue === null || conditionValue === undefined) return true;
          return !fieldValue.toLowerCase().includes(String(conditionValue).toLowerCase());
        case 'starts_with':
          if (fieldValue === null || fieldValue === undefined || typeof fieldValue !== 'string') return false;
          if (conditionValue === null || conditionValue === undefined) return false;
          return fieldValue.toLowerCase().startsWith(String(conditionValue).toLowerCase());
        case 'ends_with':
          if (fieldValue === null || fieldValue === undefined || typeof fieldValue !== 'string') return false;
          if (conditionValue === null || conditionValue === undefined) return false;
          return fieldValue.toLowerCase().endsWith(String(conditionValue).toLowerCase());
        case 'greater_than':
          if (fieldValue === null || fieldValue === undefined || conditionValue === null || conditionValue === undefined) return false;
          return Number(fieldValue) > Number(conditionValue);
        case 'less_than':
          if (fieldValue === null || fieldValue === undefined || conditionValue === null || conditionValue === undefined) return false;
          return Number(fieldValue) < Number(conditionValue);
        case 'greater_than_or_equal':
          if (fieldValue === null || fieldValue === undefined || conditionValue === null || conditionValue === undefined) return false;
          return Number(fieldValue) >= Number(conditionValue);
        case 'less_than_or_equal':
          if (fieldValue === null || fieldValue === undefined || conditionValue === null || conditionValue === undefined) return false;
          return Number(fieldValue) <= Number(conditionValue);
        case 'in':
          if (!Array.isArray(conditionValue)) return false;
          if (fieldValue === null || fieldValue === undefined) return false;
          return conditionValue.includes(fieldValue);
        case 'not_in':
          if (!Array.isArray(conditionValue)) return true;
          if (fieldValue === null || fieldValue === undefined) return true;
          return !conditionValue.includes(fieldValue);
        case 'is_empty':
          return fieldValue === null || fieldValue === undefined || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0);
        case 'is_not_empty':
          return fieldValue !== null && fieldValue !== undefined && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0);
        case 'date_before':
          const beforeDate = fieldValue instanceof Date
            ? fieldValue
            : (fieldValue && typeof fieldValue === 'object' && 'toDate' in fieldValue && typeof fieldValue.toDate === 'function')
              ? (fieldValue as any).toDate()
              : null;
          if (!beforeDate) return false;

          let beforeCondition: Date | null = null;
          if (conditionValue instanceof Date) {
            beforeCondition = conditionValue;
          } else if (conditionValue && typeof conditionValue === 'object' && 'toDate' in conditionValue && typeof (conditionValue as any).toDate === 'function') {
            beforeCondition = (conditionValue as any).toDate();
          } else if (conditionValue !== null && conditionValue !== undefined) {
            beforeCondition = new Date(String(conditionValue));
          }
          if (!beforeCondition || isNaN(beforeCondition.getTime())) return false;
          return beforeDate < beforeCondition;

        case 'date_after':
          const afterDate = fieldValue instanceof Date
            ? fieldValue
            : (fieldValue && typeof fieldValue === 'object' && 'toDate' in fieldValue && typeof fieldValue.toDate === 'function')
              ? (fieldValue as any).toDate()
              : null;
          if (!afterDate) return false;

          let afterCondition: Date | null = null;
          if (conditionValue instanceof Date) {
            afterCondition = conditionValue;
          } else if (conditionValue && typeof conditionValue === 'object' && 'toDate' in conditionValue && typeof (conditionValue as any).toDate === 'function') {
            afterCondition = (conditionValue as any).toDate();
          } else if (conditionValue !== null && conditionValue !== undefined) {
            afterCondition = new Date(String(conditionValue));
          }
          if (!afterCondition || isNaN(afterCondition.getTime())) return false;
          return afterDate > afterCondition;

        case 'date_between':
          if (Array.isArray(conditionValue) && conditionValue.length === 2) {
            const [start, end] = conditionValue as [any, any];
            const betweenDate = fieldValue instanceof Date
              ? fieldValue
              : (fieldValue && typeof fieldValue === 'object' && 'toDate' in fieldValue && typeof fieldValue.toDate === 'function')
                ? (fieldValue as any).toDate()
                : null;
            if (!betweenDate) return false;

            let startDate: Date | null = null;
            if (start instanceof Date) {
              startDate = start;
            } else if (start && typeof start === 'object' && 'toDate' in start && typeof (start as any).toDate === 'function') {
              startDate = (start as any).toDate();
            } else if (start !== null && start !== undefined) {
              startDate = new Date(String(start));
            }

            let endDate: Date | null = null;
            if (end instanceof Date) {
              endDate = end;
            } else if (end && typeof end === 'object' && 'toDate' in end && typeof (end as any).toDate === 'function') {
              endDate = (end as any).toDate();
            } else if (end !== null && end !== undefined) {
              endDate = new Date(String(end));
            }

            if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
              return false;
            }
            return betweenDate >= startDate && betweenDate <= endDate;
          }
          return false;
        case 'days_ago':
          if (fieldValue === null || fieldValue === undefined) return false;
          if (conditionValue === null || conditionValue === undefined) return false;

          const daysAgo = Number(conditionValue);
          if (isNaN(daysAgo) || daysAgo < 0) return false;

          let dateValue: Date | null = null;

          if (fieldValue instanceof Date) {
            dateValue = fieldValue;
          } else if (fieldValue && typeof fieldValue === 'object' && 'toDate' in fieldValue && typeof (fieldValue as any).toDate === 'function') {
            try {
              dateValue = (fieldValue as any).toDate();
            } catch (error) {
              console.warn(`Error converting timestamp to date:`, error);
              return false;
            }
          }

          if (!dateValue || isNaN(dateValue.getTime())) return false;

          const cutoffDate = new Date();
          cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
          cutoffDate.setHours(23, 59, 59, 999); // End of day
          return dateValue <= cutoffDate;
        case 'has_any':
          return Array.isArray(fieldValue) && Array.isArray(conditionValue) &&
            conditionValue.some(val => fieldValue.includes(val));
        case 'has_all':
          return Array.isArray(fieldValue) && Array.isArray(conditionValue) &&
            conditionValue.every(val => fieldValue.includes(val));
        case 'has_none':
          return Array.isArray(fieldValue) && Array.isArray(conditionValue) &&
            !conditionValue.some(val => fieldValue.includes(val));
        default:
          console.warn(`Unknown operator "${condition.operator}" for field "${normalizedField}"`);
          return false;
      }
    } catch (error) {
      console.error(`Error evaluating condition on field "${condition.field}" with operator "${condition.operator}":`, error);
      return false; // Fail safely - don't include contact if evaluation fails
    }
  }

  // Get field value from contact object with robust error handling
  private getFieldValue(contact: Contact & { ecommerce?: EcommerceData }, fieldPath: string): any {
    try {
      if (!contact || !fieldPath) {
        return null;
      }

      // Map segment field IDs to actual contact document property names
      const fieldAliases: Record<string, string> = {
        'status': 'subscriptionStatus'
      };
      const resolvedPath = fieldAliases[fieldPath] || fieldPath;

      const parts = resolvedPath.split('.');
      let value: any = contact;

      for (const part of parts) {
        if (value && typeof value === 'object' && value !== null) {
          value = value[part];
        } else {
          return null;
        }
      }

      // Handle Timestamp objects (Firestore Timestamp)
      if (value && typeof value === 'object' && value.toDate && typeof value.toDate === 'function') {
        try {
          return value.toDate();
        } catch (error) {
          console.warn(`Error converting timestamp for field "${fieldPath}":`, error);
          return null;
        }
      }

      // Handle null/undefined
      if (value === null || value === undefined) {
        return null;
      }

      return value;
    } catch (error) {
      console.error(`Error getting field value for "${fieldPath}":`, error);
      return null;
    }
  }

  // Get ecommerce data for contacts
  private async getEcommerceData(contactIds: string[]): Promise<{ [contactId: string]: EcommerceData }> {
    try {
      if (contactIds.length === 0) return {};

      const ecommerceData: { [contactId: string]: EcommerceData } = {};
      const ecommerceRef = collection(db, this.ECOMMERCE_COLLECTION);

      // Firestore 'in' queries are limited to 10 items, so we need to batch them
      const BATCH_SIZE = 10;

      for (let i = 0; i < contactIds.length; i += BATCH_SIZE) {
        const batch = contactIds.slice(i, i + BATCH_SIZE);
        const q = query(ecommerceRef, where('contactId', 'in', batch));
        const querySnapshot = await getDocs(q);

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          ecommerceData[data.contactId] = data as EcommerceData;
        });
      }

      return ecommerceData;
    } catch (error) {
      console.error('Error getting ecommerce data:', error);
      return {};
    }
  }

  // Get segment analytics
  async getSegmentAnalytics(segmentId: string, period: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<SegmentAnalytics> {
    try {
      const segment = await this.getSegment(segmentId);
      if (!segment) {
        throw new Error('Segment not found');
      }

      const contacts = await this.getSegmentContacts(segmentId);

      // Calculate engagement rate
      let totalSent = 0;
      let totalOpened = 0;
      contacts.forEach(contact => {
        totalSent += contact.totalEmailsSent || 0;
        totalOpened += contact.totalEmailsOpened || 0;
      });
      const engagementRate = totalSent > 0 ? (totalOpened / totalSent) * 100 : 0;

      // Calculate growth rate (contacts added in the period vs total)
      const periodDays = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : 365;
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - periodDays);

      const recentContacts = contacts.filter(contact => {
        if (!contact.addedAt) return false;
        const addedAt = contact.addedAt as any;
        const addedDate = addedAt?.toDate ? addedAt.toDate() : (addedAt?.seconds ? new Date(addedAt.seconds * 1000) : (addedAt instanceof Date ? addedAt : new Date(0)));
        return addedDate > cutoffDate;
      });

      const growthRate = contacts.length > 0 ? (recentContacts.length / contacts.length) * 100 : 0;

      // Calculate revenue (if ecommerce data is available)
      let totalRevenue = 0;
      contacts.forEach(contact => {
        if (contact.ecommerce && contact.ecommerce.totalSpent) {
          totalRevenue += contact.ecommerce.totalSpent;
        }
      });

      // Calculate analytics
      const analytics: SegmentAnalytics = {
        segmentId,
        period,
        contactCount: contacts.length,
        growthRate: Math.round(growthRate * 10) / 10,
        engagementRate: Math.round(engagementRate * 10) / 10,
        revenue: Math.round(totalRevenue * 100) / 100,
        topTags: [],
        topSources: [],
        lastActivity: segment.updatedAt
      };

      // Calculate top tags
      const tagCounts: { [tagId: string]: number } = {};
      contacts.forEach(contact => {
        contact.tags?.forEach(tagId => {
          tagCounts[tagId] = (tagCounts[tagId] || 0) + 1;
        });
      });

      analytics.topTags = Object.entries(tagCounts)
        .map(([tagId, count]) => ({ tagId, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Calculate top sources
      const sourceCounts: { [source: string]: number } = {};
      contacts.forEach(contact => {
        const source = contact.source || 'unknown';
        sourceCounts[source] = (sourceCounts[source] || 0) + 1;
      });

      analytics.topSources = Object.entries(sourceCounts)
        .map(([source, count]) => ({ source, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      return analytics;
    } catch (error) {
      console.error('Error getting segment analytics:', error);
      throw new Error('Failed to get segment analytics');
    }
  }

  // Get segment usage statistics
  async getSegmentUsage(segmentId: string): Promise<SegmentUsage> {
    try {
      // TODO: Implement usage tracking
      // This would require tracking segment usage in campaigns and automations
      return {
        segmentId,
        usedInCampaigns: 0,
        usedInAutomations: 0,
        totalUses: 0
      };
    } catch (error) {
      console.error('Error getting segment usage:', error);
      throw new Error('Failed to get segment usage');
    }
  }

  // Bulk evaluate all dynamic segments
  async bulkEvaluateSegments(workspaceId: string): Promise<void> {
    try {
      const segments = await this.getSegments({
        workspaceId,
        isDynamic: true,
        isActive: true
      });

      const batch = writeBatch(db);
      const promises = segments.map(segment => this.evaluateSegment(segment.id));

      await Promise.all(promises);
    } catch (error) {
      console.error('Error bulk evaluating segments:', error);
      throw new Error('Failed to bulk evaluate segments');
    }
  }

  // Listen to segment changes
  onSegmentChange(workspaceId: string, callback: (segments: Segment[]) => void): Unsubscribe {
    const segmentsRef = collection(db, this.COLLECTION_NAME);
    const q = query(
      segmentsRef,
      where('workspaceId', '==', workspaceId),
      orderBy('updatedAt', 'desc')
    );

    return onSnapshot(q, (querySnapshot) => {
      const segments: Segment[] = [];
      querySnapshot.forEach((doc) => {
        segments.push({
          id: doc.id,
          ...doc.data()
        } as Segment);
      });
      callback(segments);
    });
  }

  // Get available segment fields
  getAvailableFields(): typeof AVAILABLE_SEGMENT_FIELDS {
    return AVAILABLE_SEGMENT_FIELDS;
  }

  // Validate segment conditions with comprehensive checks
  validateSegmentConditions(conditionGroups: SegmentConditionGroup[]): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!conditionGroups || conditionGroups.length === 0) {
      errors.push('At least one condition group is required');
      return { isValid: false, errors };
    }

    conditionGroups.forEach((group, groupIndex) => {
      if (!group || !group.conditions || group.conditions.length === 0) {
        errors.push(`Condition group ${groupIndex + 1} must have at least one condition`);
        return;
      }

      if (!group.logic || !['AND', 'OR'].includes(group.logic)) {
        errors.push(`Condition group ${groupIndex + 1} must have a valid logic operator (AND or OR)`);
      }

      group.conditions.forEach((condition, conditionIndex) => {
        if (!condition.field) {
          errors.push(`Condition ${conditionIndex + 1} in group ${groupIndex + 1} is missing a field`);
          return;
        }

        if (!condition.operator) {
          errors.push(`Condition ${conditionIndex + 1} in group ${groupIndex + 1} is missing an operator`);
          return;
        }

        const field = AVAILABLE_SEGMENT_FIELDS.find(f => f.id === condition.field);
        if (!field) {
          errors.push(`Invalid field "${condition.field}" in condition ${conditionIndex + 1} of group ${groupIndex + 1}`);
          return;
        }

        // Validate operator compatibility
        if (!field.operators.includes(condition.operator)) {
          errors.push(`Invalid operator "${condition.operator}" for field "${field.label}" in condition ${conditionIndex + 1} of group ${groupIndex + 1}. Valid operators: ${field.operators.join(', ')}`);
        }

        // Validate value based on field type and operator
        if (!['is_empty', 'is_not_empty'].includes(condition.operator)) {
          if (condition.value === null || condition.value === undefined) {
            errors.push(`Condition ${conditionIndex + 1} in group ${groupIndex + 1} requires a value for operator "${condition.operator}"`);
          } else {
            // Type-specific validation
            if (field.type === 'number' && typeof condition.value !== 'number') {
              errors.push(`Field "${field.label}" expects a number value, got ${typeof condition.value}`);
            }

            if (field.type === 'date' && condition.operator === 'date_between') {
              if (!Array.isArray(condition.value) || condition.value.length !== 2) {
                errors.push(`Field "${field.label}" with operator "date_between" requires an array of two dates`);
              }
            }

            if (field.type === 'array' && ['has_any', 'has_all', 'has_none'].includes(condition.operator)) {
              if (!Array.isArray(condition.value)) {
                errors.push(`Field "${field.label}" with operator "${condition.operator}" requires an array value`);
              }
            }

            if (field.type === 'select' && ['in', 'not_in'].includes(condition.operator)) {
              if (!Array.isArray(condition.value)) {
                errors.push(`Field "${field.label}" with operator "${condition.operator}" requires an array value`);
              }
            }
          }
        }
      });
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Automation-specific methods for segment management

  // Add contact to segment (for automation actions)
  async addContactToSegment(contactId: string, segmentId: string): Promise<void> {
    try {
      const contact = await this.getContact(contactId);
      if (!contact) throw new Error('Contact not found');

      const segment = await this.getSegmentById(segmentId);
      if (!segment) throw new Error('Segment not found');

      // Get current segment IDs or initialize empty array
      const currentSegments = contact.segments || [];

      // Add segment if not already present
      if (!currentSegments.includes(segmentId)) {
        const updatedSegments = [...currentSegments, segmentId];

        await updateDoc(doc(db, this.CONTACTS_COLLECTION, contactId), {
          segments: updatedSegments,
          lastChanged: serverTimestamp()
        });

        // Update segment contact count - use increment to avoid race conditions
        // But first verify the count is accurate by checking if this is a dynamic segment
        // For dynamic segments, we'll let evaluateSegment handle the count
        if (!segment.isDynamic) {
          await updateDoc(doc(db, this.COLLECTION_NAME, segmentId), {
            contactCount: (segment.contactCount || 0) + 1,
            updatedAt: serverTimestamp()
          });
        }

        console.log(`Added contact ${contactId} to segment ${segmentId}`);
      }
    } catch (error) {
      console.error('Error adding contact to segment:', error);
      throw error;
    }
  }

  // Remove contact from segment (for automation actions)
  async removeContactFromSegment(contactId: string, segmentId: string): Promise<void> {
    try {
      const contact = await this.getContact(contactId);
      if (!contact) throw new Error('Contact not found');

      const segment = await this.getSegmentById(segmentId);
      if (!segment) throw new Error('Segment not found');

      const currentSegments = contact.segments || [];

      // Remove segment if present
      if (currentSegments.includes(segmentId)) {
        const updatedSegments = currentSegments.filter((id: string) => id !== segmentId);

        await updateDoc(doc(db, this.CONTACTS_COLLECTION, contactId), {
          segments: updatedSegments,
          lastChanged: serverTimestamp()
        });

        // Update segment contact count - for dynamic segments, let evaluateSegment handle it
        if (!segment.isDynamic) {
          await updateDoc(doc(db, this.COLLECTION_NAME, segmentId), {
            contactCount: Math.max(0, (segment.contactCount || 0) - 1),
            updatedAt: serverTimestamp()
          });
        }

        console.log(`Removed contact ${contactId} from segment ${segmentId}`);
      }
    } catch (error) {
      console.error('Error removing contact from segment:', error);
      throw error;
    }
  }

  // Get contact by ID (helper method)
  private async getContact(contactId: string): Promise<any> {
    const contactDoc = await getDoc(doc(db, this.CONTACTS_COLLECTION, contactId));
    return contactDoc.exists() ? { id: contactDoc.id, ...contactDoc.data() } : null;
  }

  // Get contacts in a segment (for automation targeting)
  async getContactsInSegment(segmentId: string, limit?: number): Promise<any[]> {
    try {
      const contactsRef = collection(db, this.CONTACTS_COLLECTION);
      let q = query(
        contactsRef,
        where('segments', 'array-contains', segmentId)
      );

      if (limit) {
        q = query(q, orderBy('addedAt', 'desc'), firestoreLimit(limit));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting contacts in segment:', error);
      return [];
    }
  }

  // Check if contact matches segment conditions (for real-time evaluation)
  async contactMatchesSegment(contactId: string, segmentId: string): Promise<boolean> {
    try {
      const contact = await this.getContact(contactId);
      if (!contact) return false;

      const segment = await this.getSegmentById(segmentId);
      if (!segment) return false;

      // Get ecommerce data if available
      const ecommerceData = await this.getEcommerceData([contactId]);
      const contactWithEcommerce = { ...contact, ecommerce: ecommerceData[contactId] };

      return this.evaluateContactAgainstConditions(contactWithEcommerce, segment.conditionGroups);
    } catch (error) {
      console.error('Error checking if contact matches segment:', error);
      return false;
    }
  }

  // Batch add contacts to segment (for automation workflows)
  async batchAddContactsToSegment(contactIds: string[], segmentId: string): Promise<{
    success: number;
    failed: number;
    errors: string[];
  }> {
    const result = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    for (const contactId of contactIds) {
      try {
        await this.addContactToSegment(contactId, segmentId);
        result.success++;
      } catch (error) {
        result.failed++;
        result.errors.push(`Failed to add contact ${contactId}: ${error}`);
      }
    }

    return result;
  }

  // Get segment by ID (helper method)
  async getSegmentById(segmentId: string): Promise<Segment | null> {
    try {
      const segmentDoc = await getDoc(doc(db, this.COLLECTION_NAME, segmentId));
      return segmentDoc.exists() ? { id: segmentDoc.id, ...segmentDoc.data() } as Segment : null;
    } catch (error) {
      console.error('Error getting segment:', error);
      return null;
    }
  }

  // Evaluate a single contact against all dynamic segments in a workspace
  async evaluateContactForSegments(contactId: string, workspaceId: string): Promise<void> {
    try {
      const contact = await this.getContact(contactId);
      if (!contact || contact.workspaceId !== workspaceId) {
        console.log(`[Segment] Skipping evaluation: contact ${contactId} not found or wrong workspace`);
        return;
      }

      // Get all dynamic segments for this workspace
      const dynamicSegments = await this.getSegments({
        workspaceId,
        isDynamic: true,
        isActive: true
      });

      console.log(`[Segment] Evaluating contact ${contactId} against ${dynamicSegments.length} dynamic segments`);

      if (dynamicSegments.length === 0) {
        return;
      }

      // Get ecommerce data if available
      const ecommerceData = await this.getEcommerceData([contactId]);
      const contactWithEcommerce = { ...contact, ecommerce: ecommerceData[contactId] };

      // Get current segments
      const currentSegments = contact.segments || [];

      // Evaluate contact against each segment
      for (const segment of dynamicSegments) {
        try {
          const matches = this.evaluateContactAgainstConditions(contactWithEcommerce, segment.conditionGroups);
          const isInSegment = currentSegments.includes(segment.id);

          console.log(`[Segment] Contact ${contactId} vs segment "${segment.name}": matches=${matches}, isInSegment=${isInSegment}`);

          if (matches && !isInSegment) {
            // Add to segment
            console.log(`[Segment] Adding contact ${contactId} to segment ${segment.id}`);
            await this.addContactToSegment(contactId, segment.id);
          } else if (!matches && isInSegment) {
            // Remove from segment
            console.log(`[Segment] Removing contact ${contactId} from segment ${segment.id}`);
            await this.removeContactFromSegment(contactId, segment.id);
          }
        } catch (segmentError) {
          console.error(`[Segment] Error evaluating contact ${contactId} against segment ${segment.id}:`, segmentError);
          // Continue with other segments
        }
      }
    } catch (error) {
      console.error('[Segment] Error evaluating contact for segments:', error);
      // Don't throw - this is a background operation
    }
  }

  // Auto-segment contacts based on behavior (for automation triggers)
  async autoSegmentContact(contactId: string, behaviorType: 'purchase' | 'engagement' | 'activity', metadata?: any): Promise<string[]> {
    const addedSegments: string[] = [];

    try {
      const contact = await this.getContact(contactId);
      if (!contact) return addedSegments;

      // Get all dynamic segments
      const segments = await this.getSegments({ isDynamic: true });

      for (const segment of segments) {
        // Check if contact should be in this segment based on behavior
        const shouldBeInSegment = await this.evaluateContactBehavior(contact, segment, behaviorType, metadata);

        const isInSegment = contact.segments?.includes(segment.id);

        if (shouldBeInSegment && !isInSegment) {
          await this.addContactToSegment(contactId, segment.id!);
          addedSegments.push(segment.id!);
        } else if (!shouldBeInSegment && isInSegment) {
          await this.removeContactFromSegment(contactId, segment.id!);
        }
      }
    } catch (error) {
      console.error('Error auto-segmenting contact:', error);
    }

    return addedSegments;
  }

  // Evaluate contact behavior for segment membership
  private async evaluateContactBehavior(contact: any, segment: Segment, behaviorType: string, metadata?: any): Promise<boolean> {
    // Check if segment has condition groups
    if (!segment.conditionGroups || segment.conditionGroups.length === 0) return false;

    // Get ecommerce data if available
    const ecommerceData = await this.getEcommerceData([contact.id]);
    const contactWithEcommerce = { ...contact, ecommerce: ecommerceData[contact.id] };

    // Use the standard evaluation with conditionGroups
    return this.evaluateContactAgainstConditions(contactWithEcommerce, segment.conditionGroups);
  }

  // Listen to segment membership changes (for automation triggers)
  // workspaceId scopes the query to one workspace — without it every contact
  // across all users whose segments array contains this segmentId is streamed,
  // billing a read for each on the initial snapshot.
  subscribeToSegmentChanges(
    segmentId: string,
    callback: (contactIds: string[]) => void,
    workspaceId?: string
  ): Unsubscribe {
    const contactsRef = collection(db, this.CONTACTS_COLLECTION);
    const constraints: any[] = [where('segments', 'array-contains', segmentId)];
    if (workspaceId) constraints.push(where('workspaceId', '==', workspaceId));
    // Cap at 1000 — large segments should use server-side evaluation, not a client listener.
    constraints.push(firestoreLimit(1000));
    const q = query(contactsRef, ...constraints);

    return onSnapshot(q, (snapshot) => {
      const contactIds = snapshot.docs.map(doc => doc.id);
      callback(contactIds);
    }, (error) => {
      console.error(`Error in segment subscription for ${segmentId}:`, error);
    });
  }

  // Health check for a segment - validates conditions and checks for issues
  async checkSegmentHealth(segmentId: string): Promise<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
    contactCount: number;
    lastEvaluated?: Date;
  }> {
    try {
      const segment = await this.getSegment(segmentId);
      if (!segment) {
        return {
          isValid: false,
          errors: ['Segment not found'],
          warnings: [],
          contactCount: 0
        };
      }

      const errors: string[] = [];
      const warnings: string[] = [];

      // Validate conditions
      const validation = this.validateSegmentConditions(segment.conditionGroups);
      if (!validation.isValid) {
        errors.push(...validation.errors);
      }

      // Check if segment is active but hasn't been evaluated recently (for dynamic segments)
      if (segment.isDynamic && segment.isActive) {
        if (!segment.lastEvaluated) {
          warnings.push('Dynamic segment has never been evaluated');
        } else {
          const daysSinceEvaluation = (Date.now() - segment.lastEvaluated.toMillis()) / (1000 * 60 * 60 * 24);
          if (daysSinceEvaluation > 7) {
            warnings.push(`Segment hasn't been evaluated in ${Math.floor(daysSinceEvaluation)} days`);
          }
        }
      }

      // Check for empty segments
      if (segment.contactCount === 0 && segment.isActive) {
        warnings.push('Segment is active but contains no contacts');
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings,
        contactCount: segment.contactCount,
        lastEvaluated: segment.lastEvaluated?.toDate()
      };
    } catch (error) {
      console.error('Error checking segment health:', error);
      return {
        isValid: false,
        errors: [`Failed to check segment health: ${error instanceof Error ? error.message : 'Unknown error'}`],
        warnings: [],
        contactCount: 0
      };
    }
  }

  // Get segment statistics
  async getSegmentStats(segmentId: string): Promise<{
    contactCount: number;
    lastEvaluated?: Date;
    evaluationTime?: number;
    isHealthy: boolean;
  }> {
    try {
      const segment = await this.getSegment(segmentId);
      if (!segment) {
        throw new Error('Segment not found');
      }

      const health = await this.checkSegmentHealth(segmentId);

      return {
        contactCount: segment.contactCount,
        lastEvaluated: segment.lastEvaluated?.toDate(),
        isHealthy: health.isValid && health.warnings.length === 0
      };
    } catch (error) {
      console.error('Error getting segment stats:', error);
      throw new Error(`Failed to get segment stats: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

export const segmentService = new SegmentService();
